import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useVisualViewport } from "@/hooks/useVisualViewport";
import { BRAND } from "@/lib/brand";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { MessagesSquare, Library, Activity, ScrollText, LogOut, Users, KeyRound, Menu } from "lucide-react";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  reviewer: "Reviewer",
  loan_officer: "Loan Officer",
  underwriter: "Loan Officer",
};
export const isAdminRole = (role) => role === "admin" || role === "super_admin";

function NavList({ links, pathname, onNavigate, prefix = "nav" }) {
  return (
    <nav className="flex flex-col gap-0.5 p-3">
      {links.map((l) => {
        const active = pathname === l.to;
        const Icon = l.icon;
        return (
          <Link key={l.to} to={l.to} onClick={onNavigate} data-testid={`${prefix}-${l.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
              active
                ? "bg-white text-[#05304F] font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                : "text-[#303030] hover:bg-[#E3E5E7]"
            }`}>
            <Icon className={`h-4 w-4 ${active ? "text-[#05304F]" : "text-[#6D7175]"}`} /> {l.label}
          </Link>
        );
      })}
    </nav>
  );
}

function UserCard({ user }) {
  return (
    <div className="m-3 p-3 rounded-lg bg-white border border-[#E1E3E5]" data-testid="sidebar-user-card">
      <div className="text-sm font-semibold text-[#202223] truncate">{user?.name}</div>
      <div className="text-xs text-[#6D7175] truncate">{user?.email}</div>
      <span className="inline-block mt-2 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#F0F7FF] text-[#0369A1] border border-[#BAE6FD]"
        data-testid="user-role-badge">
        {ROLE_LABEL[user?.role] || user?.role}
      </span>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  useVisualViewport();
  const [pwOpen, setPwOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isAdmin = isAdminRole(user?.role);
  const canReview = isAdmin || user?.role === "reviewer";

  const links = [
    { to: "/research", label: "Research", icon: MessagesSquare, show: true },
    { to: "/library", label: "Library", icon: Library, show: true },
    { to: "/jobs", label: "Ingestion", icon: Activity, show: canReview },
    { to: "/users", label: "Users", icon: Users, show: isAdmin },
    { to: "/audit", label: "Audit", icon: ScrollText, show: isAdmin },
  ].filter((l) => l.show);

  return (
    <div
      className="fixed left-0 right-0 flex flex-col bg-background overflow-hidden"
      style={{ top: "var(--app-top, 0px)", height: "var(--app-height, 100dvh)" }}
    >
      <header className="bg-[#05304F] text-white flex items-center justify-between pl-2 pr-3 sm:px-4 h-14 shrink-0 border-b-2 border-[#E0A430] font-polaris">
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="icon" onClick={() => setMenuOpen(true)} data-testid="mobile-nav-toggle"
            className="md:hidden text-white hover:bg-white/10 hover:text-white" aria-label="Open navigation">
            <Menu className="h-5 w-5" />
          </Button>
          <Link to="/research" className="flex items-center gap-2.5" data-testid="brand-link">
            <img src={BRAND.logos.mark} alt="Freedmont Mortgage" className="h-8 w-8 object-contain" />
            <span className="leading-tight">
              <span className="block font-semibold text-base text-white">{BRAND.shortName}</span>
              <span className="block text-[11px] text-white/65">{BRAND.tagline}</span>
            </span>
          </Link>
        </div>
        <div className="flex items-center gap-1">
          <span className="hidden sm:block text-sm text-white/85 mr-2" data-testid="topbar-user-name">{user?.name}</span>
          <Button variant="ghost" size="icon" onClick={() => setPwOpen(true)} data-testid="change-password-button" title="Change password"
            className="text-white/80 hover:text-white hover:bg-white/10">
            <KeyRound className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button" title="Log out"
            className="text-white/80 hover:text-white hover:bg-white/10">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <aside className="hidden md:flex w-56 shrink-0 flex-col justify-between bg-[#EBEBEB] border-r border-[#E1E3E5] font-polaris" data-testid="sidebar">
          <NavList links={links} pathname={loc.pathname} />
          <UserCard user={user} />
        </aside>
        <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">{children}</main>
      </div>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-[#EBEBEB] flex flex-col justify-between font-polaris" data-testid="mobile-nav">
          <div>
            <SheetTitle className="px-6 pt-5 text-base font-semibold text-[#202223]">Navigation</SheetTitle>
            <NavList links={links} pathname={loc.pathname} onNavigate={() => setMenuOpen(false)} prefix="mobile-nav" />
          </div>
          <UserCard user={user} />
        </SheetContent>
      </Sheet>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}

export { ROLE_LABEL };
